import React, { useEffect, useState, useCallback } from 'react';
import cn from '../../utils/classNames';

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  color: string;
  shape: 'circle' | 'square' | 'triangle' | 'star';
  animationDelay: number;
  animationDuration: number;
  velocityX: number;
  velocityY: number;
}

interface ConfettiAnimationProps {
  isActive: boolean;
  duration?: number; // Duración en milisegundos
  particleCount?: number;
  onComplete?: () => void;
}

const CONFETTI_COLORS = [
  '#FFD700', // Oro
  '#FFA500', // Naranja
  '#FF6B6B', // Rojo coral
  '#4ECDC4', // Turquesa
  '#45B7D1', // Azul
  '#96CEB4', // Verde menta
  '#FFEAA7', // Amarillo claro
  '#DDA0DD', // Ciruela
  '#98D8C8', // Menta
  '#F7DC6F', // Amarillo dorado
];

// Función para generar una pieza de confeti aleatoria
const generateConfettiPiece = (id: number): ConfettiPiece => {
  const shapes: ConfettiPiece['shape'][] = ['circle', 'square', 'triangle', 'star'];
  
  return {
    id,
    x: Math.random() * 100, // Posición X en porcentaje
    y: -10, // Comienza arriba de la pantalla
    rotation: Math.random() * 360,
    scale: 0.6 + Math.random() * 0.6, // Escala entre 0.6 y 1.2
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    shape: shapes[Math.floor(Math.random() * shapes.length)],
    animationDelay: Math.random() * 1500, // Delay hasta 1.5 segundos
    animationDuration: 3000 + Math.random() * 2000, // Duración entre 3-5 segundos
    velocityX: (Math.random() - 0.5) * 2, // Velocidad horizontal
    velocityY: 2 + Math.random() * 3, // Velocidad vertical
  };
};

// Componente para renderizar una pieza individual de confeti
const ConfettiPieceComponent: React.FC<{ piece: ConfettiPiece; isActive: boolean }> = React.memo(({ 
  piece, 
  isActive 
}) => {
  const getShapeStyles = useCallback(() => {
    const baseStyles: React.CSSProperties = {
      width: '10px',
      height: '10px',
      position: 'absolute',
      left: `${piece.x}%`,
      top: `${piece.y}px`,
      backgroundColor: piece.color,
      transform: `rotate(${piece.rotation}deg) scale(${piece.scale})`,
      animation: isActive 
        ? `confetti-fall-${piece.id} ${piece.animationDuration}ms ease-out ${piece.animationDelay}ms forwards`
        : 'none',
      willChange: 'transform, opacity',
    };

    switch (piece.shape) {
      case 'circle':
        return {
          ...baseStyles,
          borderRadius: '50%',
        };
      case 'square':
        return baseStyles;
      case 'triangle':
        return {
          ...baseStyles,
          width: '0',
          height: '0',
          backgroundColor: 'transparent',
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderBottom: `10px solid ${piece.color}`,
        };
      case 'star':
        return {
          ...baseStyles,
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        };
      default:
        return baseStyles;
    }
  }, [piece, isActive]);

  return (
    <div
      style={getShapeStyles()}
      className="pointer-events-none"
    />
  );
});

ConfettiPieceComponent.displayName = 'ConfettiPieceComponent';

const ConfettiAnimation: React.FC<ConfettiAnimationProps> = ({
  isActive,
  duration = 4000,
  particleCount = 50,
  onComplete,
}) => {
  const [confettiPieces, setConfettiPieces] = useState<ConfettiPiece[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  // Generar keyframes dinámicos para cada pieza
  const generateKeyframes = useCallback((pieces: ConfettiPiece[]) => {
    return pieces.map(piece => `
      @keyframes confetti-fall-${piece.id} {
        0% {
          transform: translateY(-100px) translateX(0px) rotate(0deg) scale(${piece.scale});
          opacity: 1;
        }
        10% {
          opacity: 1;
        }
        90% {
          opacity: 1;
        }
        100% {
          transform: translateY(calc(100vh + 50px)) translateX(${piece.velocityX * 100}px) rotate(${720 + piece.rotation}deg) scale(${piece.scale * 0.8});
          opacity: 0;
        }
      }
    `).join('\n');
  }, []);

  useEffect(() => {
    if (isActive && !isAnimating) {
      // Generar piezas de confeti
      const pieces = Array.from({ length: particleCount }, (_, i) => 
        generateConfettiPiece(i)
      );
      setConfettiPieces(pieces);
      setIsAnimating(true);

      // Limpiar después de la duración especificada
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setConfettiPieces([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isActive, duration, particleCount, onComplete, isAnimating]);

  if (!isActive && !isAnimating) {
    return null;
  }

  return (
    <>
      {/* Estilos CSS dinámicos para las animaciones */}
      <style>
        {`
          ${generateKeyframes(confettiPieces)}

          .confetti-container {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 9999;
            overflow: hidden;
          }

          /* Optimizaciones para rendimiento */
          .confetti-piece {
            backface-visibility: hidden;
            perspective: 1000px;
            transform-style: preserve-3d;
          }

          /* Soporte para navegadores más antiguos */
          @supports not (clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)) {
            .confetti-star {
              width: 0;
              height: 0;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-bottom: 10px solid currentColor;
            }
          }

          /* Media query para dispositivos con movimiento reducido */
          @media (prefers-reduced-motion: reduce) {
            .confetti-container {
              display: none;
            }
          }

          /* Optimización para dispositivos móviles */
          @media (max-width: 768px) {
            .confetti-container {
              transform: scale(0.8);
            }
          }
        `}
      </style>

      {/* Contenedor de confeti */}
      <div className="confetti-container">
        {confettiPieces.map((piece) => (
          <ConfettiPieceComponent
            key={piece.id}
            piece={piece}
            isActive={isAnimating}
          />
        ))}
      </div>
    </>
  );
};

export default ConfettiAnimation;